import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import inventoryApi from "../../services/inventory.api.js";
import { titleCase } from "../../utils/format.js";
import useAuth from "../../context/useAuth.js";
import { ReminderNotificationButton } from "../reminders/ReminderNotifications.jsx";
import Avatar from "../ui/Avatar.jsx";
import UserMenu from "./UserMenu.jsx";

const TITLES = [
  [/^\/dashboard/, "Dashboard"],
  [/^\/profile/, "My Profile & Account"],
  [/^\/quick-sales/, "Quick Sales"],
  [/^\/clients\/.+\/edit/, "Edit Client"],
  [/^\/clients\/new/, "New Client"],
  [/^\/clients\/.+/, "Client Profile"],
  [/^\/clients/, "Clients Module"],
  [/^\/appointments\/new/, "New Appointment"],
  [/^\/appointments/, "Appointments"],
  [/^\/employees/, "Staff"],
  [/^\/staff/, "Staff Management"],
  [/^\/my-attendance/, "My Attendance & Leaves"],
  [/^\/admin\/services/, "Services"],
  [/^\/inventory/, "Products"],
  [/^\/payments/, "Payments"],
  [/^\/expenses/, "Expenses"],
  [/^\/reports/, "Reports"],
];

const titleFor = (pathname) =>
  TITLES.find(([pattern]) => pattern.test(pathname))?.[1] ?? "Swastik Professional Salon";

/**
 * TopNavBar — ported from `frontend/premium_salon_management_system/code.html`:
 * `h-20 glass-nav`, module name on the left, utilities on the right.
 *
 * On mobile this single pinned bar also carries the drawer trigger, so the
 * navigation stays one tap away no matter how far the page is scrolled (a
 * separate brand strip would either scroll out of reach or double the pinned
 * chrome). The brand itself lives in the drawer header.
 */
export default function TopNavBar({ onOpenNav }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [lowStock, setLowStock] = useState(null);

  // One cheap query that powers a real alert dot; a decorative bell would be a
  // dead control. Re-runs on navigation so a restock clears the badge.
  useEffect(() => {
    let cancelled = false;
    inventoryApi
      .getLowStock()
      .then((data) => !cancelled && setLowStock(data?.count ?? 0))
      .catch(() => !cancelled && setLowStock(0));
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return (
    <header className="glass-nav sticky top-0 z-40 flex h-20 flex-shrink-0 items-center justify-between gap-4 border-b border-outline-variant/10 px-6 lg:gap-8 lg:px-10">
      <div className="flex min-w-0 items-center gap-3">
        {onOpenNav && (
          <button
            type="button"
            onClick={onOpenNav}
            aria-label="Open navigation"
            className="-ml-2 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-on-surface transition-all duration-200 hover:bg-surface-container active:scale-90 lg:hidden"
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              menu
            </span>
          </button>
        )}
        <h1 className="truncate font-headline text-xl font-bold text-on-surface">
          {titleFor(pathname)}
        </h1>
      </div>

      <div className="flex shrink-0 items-center gap-4">
        <ReminderNotificationButton />

        <Link
          to="/inventory?filter=low-stock"
          className="relative flex h-12 w-12 items-center justify-center rounded-full text-on-surface-variant transition-all duration-200 hover:bg-surface-container active:scale-90 lg:h-10 lg:w-10"
          title={
            lowStock
              ? `${lowStock} product(s) at or below reorder level`
              : "Inventory"
          }
          aria-label={`Inventory alerts${
            lowStock ? `: ${lowStock} products low on stock` : ""
          }`}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            notifications
          </span>
          {lowStock > 0 && (
            <span className="absolute right-3 top-3 h-2 w-2 rounded-full border-2 border-surface bg-error lg:right-2 lg:top-2" />
          )}
        </Link>

        <div className="h-8 w-px bg-outline-variant/30" />

        <UserMenu>
          <button
            type="button"
            className="flex min-h-[48px] items-center gap-2.5 rounded-full py-1 pl-1 pr-1.5 transition-colors duration-200 hover:bg-surface-container active:scale-[0.98] lg:gap-3 lg:pl-2 lg:pr-2.5"
          >
            <span className="hidden text-right sm:block">
              <span className="block text-sm font-bold leading-none text-on-surface">
                {user?.name}
              </span>
              <span className="mt-1 block text-xs text-on-surface-variant">
                {titleCase(user?.role) || "Guest"}
              </span>
            </span>
            <Avatar src={user?.avatar} name={user?.name} size="md" ring />
            <span
              className="material-symbols-outlined text-base text-on-surface-variant"
              aria-hidden="true"
            >
              expand_more
            </span>
          </button>
        </UserMenu>
      </div>
    </header>
  );
}
